import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { ArrowUpRight, Play } from "lucide-react";
import { ASSETS, COMPANY, MOCK_PRODUCTS } from "@/data/mockData";
import ProductCard from "@/components/ProductCard";
import { Container, Section, Overline } from "@/components/ui-blocks";
import Seo from "@/components/Seo";
import { fetchProducts, fetchCategories } from "@/services/api";

const FEATURED_BOOK_ID = "bk-chettis-01";

// The bundled mock catalogue is only a fallback for a first paint or an
// unreachable Firestore — the admin console is the source of truth.
const FALLBACK_FEATURED = MOCK_PRODUCTS.find((p) => p.id === FEATURED_BOOK_ID);
const FALLBACK_COLLECTIBLES = MOCK_PRODUCTS.filter((p) => p.category === "collectibles").slice(0, 4);
const FALLBACK_CATEGORIES = [
  {
    id: "books",
    slug: "books",
    name: "Ace Asia Books",
    image: ASSETS.bookCoverDark,
    description:
      "Research-led volumes opening windows into communities, trade routes and traditions across the region.",
  },
  {
    id: "collectibles",
    slug: "collectibles",
    name: "Ace Asia Collectibles",
    image: ASSETS.lotusBowls,
    description:
      "Tableware, teapots and architectural facades that translate Indian Peranakan and Singapore heritage into objects of use.",
  },
];
// Used when a category exists in the admin console but has no picture or
// blurb of its own yet — both are editable under Admin → Categories.
const CATEGORY_FALLBACK_IMAGE = {
  books: ASSETS.bookCoverDark,
  collectibles: ASSETS.lotusBowls,
  podcasts: ASSETS.podcastCover,
};
const CATEGORY_FALLBACK_DESCRIPTION = {
  books:
    "Research-led volumes opening windows into communities, trade routes and traditions across the region.",
  collectibles:
    "Tableware, teapots and architectural facades that translate Indian Peranakan and Singapore heritage into objects of use.",
};

export default function Home() {
  const [featured, setFeatured] = useState(FALLBACK_FEATURED);
  const [collectibles, setCollectibles] = useState(FALLBACK_COLLECTIBLES);
  const [categories, setCategories] = useState(FALLBACK_CATEGORIES);

  useEffect(() => {
    fetchProducts()
      .then((list) => {
        if (!list.length) return;
        const book = list.find((p) => p.id === FEATURED_BOOK_ID) || list.find((p) => p.featured);
        if (book) setFeatured(book);
        const coll = list.filter((p) => p.category === "collectibles").slice(0, 4);
        if (coll.length) setCollectibles(coll);
      })
      .catch(() => {});
    fetchCategories()
      .then((list) => { if (list.length) setCategories(list); })
      .catch(() => {});
  }, []);

  return (
    <div data-testid="home-page">
      <Seo path="/" description="Ace Asia Productions — a Singapore-based research and publishing house celebrating South-east Asian heritage through books, collectibles and multimedia storytelling." />
      {/* HERO */}
      <section
        data-testid="home-hero"
        className="relative min-h-[92vh] bg-[#281a12] text-[#FDFBF7] grain overflow-hidden"
      >
        {/* Background editorial image */}
        <div className="absolute inset-0">
          <img
            src={ASSETS.founderEditorial}
            alt=""
            className="w-full h-full object-cover opacity-40 aa-slow-zoom"
          />
          <div className="absolute inset-0 bg-gradient-to-r from-[#281a12] via-[#281a12]/85 to-transparent" />
        </div>

        <div className="relative z-10 max-w-7xl mx-auto px-6 md:px-12 pt-32 pb-24 md:pt-40 md:pb-32 grid lg:grid-cols-12 gap-12 items-center">
          <div className="lg:col-span-7">
            <div className="overline text-[#946f36] mb-6 aa-fade-up aa-delay-1">
              <span className="gold-rule bg-[#946f36] mr-3" /> A Singapore Heritage House · est. 2026
            </div>
            <h1 className="font-serif-display text-5xl md:text-7xl lg:text-[5.5rem] font-light leading-[1.02] tracking-tight aa-fade-up aa-delay-2">
              Stories of South-east Asia —
              <span className="italic text-[#946f36]"> the ‘Lands of Gold’.</span>
            </h1>
            <p className="mt-7 max-w-xl text-[#D1C3B4] text-lg leading-relaxed aa-fade-up aa-delay-3">
              Ace Asia Productions is a multi-disciplinary publishing and
              creative production house dedicated to the history, heritage and
              craft of South-east Asia — beginning with the untold story of the
              Chetti Melaka. As far back as 1,800 years ago in the 2nd Century
              CE, this region was known as Suvarnabhumi, Sanskrit for “Lands of
              Gold”, and also Suvarnadvipa (Islands of Gold).
            </p>
            <div className="mt-10 flex flex-wrap gap-3 aa-fade-up aa-delay-4">
              <Link
                to="/shop/books"
                data-testid="hero-shop-books-button"
                className="bg-[#946f36] hover:bg-[#FDFBF7] hover:text-[#281a12] text-[#281a12] px-7 py-4 text-xs uppercase tracking-[0.2em] transition-colors"
              >
                Shop Books
              </Link>
              <Link
                to="/shop/collectibles"
                data-testid="hero-shop-collectibles-button"
                className="border border-[#946f36] text-[#946f36] hover:bg-[#946f36] hover:text-[#281a12] px-7 py-4 text-xs uppercase tracking-[0.2em] transition-colors"
              >
                Shop Collectibles
              </Link>
              <Link
                to="/multimedia"
                data-testid="hero-watch-podcast-button"
                className="border border-[#FDFBF7]/30 text-[#FDFBF7] hover:bg-[#FDFBF7] hover:text-[#281a12] px-7 py-4 text-xs uppercase tracking-[0.2em] transition-colors inline-flex items-center gap-2"
              >
                <Play className="w-3 h-3" strokeWidth={2} /> Watch Podcast
              </Link>
              <Link
                to="/contact"
                data-testid="hero-contact-button"
                className="text-xs uppercase tracking-[0.2em] underline-offset-4 hover:underline text-[#FDFBF7] px-2 py-4"
              >
                Contact Us
              </Link>
            </div>
          </div>

          {/* Featured book floating panel */}
          <div className="lg:col-span-5 aa-fade-up aa-delay-5">
            <div className="relative max-w-md mx-auto">
              <div className="absolute -inset-6 border border-[#946f36]/30 pointer-events-none" />
              <img
                src={featured.image || ASSETS.bookCoverLight}
                alt={`${featured.title} book cover`}
                data-testid="hero-featured-book-image"
                className="relative w-full object-cover shadow-2xl"
              />
              <div className="absolute -bottom-8 -left-6 bg-[#281a12]/85 backdrop-blur-md border border-[#946f36]/40 p-5 max-w-xs">
                <div className="overline text-[#946f36] mb-2">Featured Volume</div>
                <div className="font-serif-display text-xl leading-tight">
                  {featured.title}
                </div>
                <div className="text-xs text-[#D1C3B4] mt-1">
                  Paperback · 350 pages · September 2026
                </div>
                <Link
                  to="/featured-book"
                  data-testid="hero-featured-book-link"
                  className="inline-flex items-center gap-1.5 mt-3 text-xs uppercase tracking-[0.18em] text-[#946f36] hover:text-[#FDFBF7]"
                >
                  Explore the book <ArrowUpRight className="w-3 h-3" strokeWidth={1.5} />
                </Link>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Intro */}
      <Section testId="home-intro" className="bg-[#FDFBF7]">
        <Container>
          <div className="grid lg:grid-cols-12 gap-12 items-start">
            <div className="lg:col-span-5">
              <Overline>Our House</Overline>
              <h2 className="mt-4 font-serif-display text-4xl md:text-5xl font-light leading-tight">
                A heritage publisher with a journalist&apos;s eye.
              </h2>
            </div>
            <div className="lg:col-span-7 text-[#4A3728] leading-relaxed text-lg">
              <p>
                Founded by veteran journalist Ms Chantal Sajan, Ace Asia
                Productions is a research-led production house headquartered in
                Singapore. We promote South-east Asian history, heritage and
                culture through digital content, books, print media
                collectibles, product design and multimedia storytelling — from
                podcasts to documentaries.
              </p>
              <Link
                to="/about"
                data-testid="home-intro-about-link"
                className="inline-flex items-center gap-1.5 mt-6 overline text-[#6e5022] hover:text-[#946f36]"
              >
                Read our story <ArrowUpRight className="w-3.5 h-3.5" strokeWidth={1.5} />
              </Link>
            </div>
          </div>
        </Container>
      </Section>

      {/* Category cards */}
      <Section testId="home-categories" className="bg-[#F4F1EA]">
        <Container>
          <div className="flex items-end justify-between flex-wrap gap-6 mb-12">
            <div>
              <Overline>Browse the catalogue</Overline>
              <h2 className="mt-3 font-serif-display text-4xl md:text-5xl font-light leading-tight">
                Our collections, one heritage.
              </h2>
            </div>
          </div>

          <div
            className={`grid gap-6 lg:gap-8 md:grid-cols-2${
              categories.length > 2 ? " lg:grid-cols-3" : ""
            }`}
          >
            {categories.map((c) => (
              <Link
                key={c.id}
                to={`/shop/${c.slug}`}
                data-testid={`home-category-${c.slug}-card`}
                className="group relative overflow-hidden bg-[#281a12] aspect-[5/6] block"
              >
                <img
                  src={c.image || CATEGORY_FALLBACK_IMAGE[c.slug] || ASSETS.bookCoverDark}
                  alt={c.name}
                  className="absolute inset-0 w-full h-full object-cover opacity-70 group-hover:opacity-90 transition-opacity duration-700"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-[#281a12] via-[#281a12]/30 to-transparent" />
                <div className="relative z-10 h-full flex flex-col justify-end p-8 md:p-10 text-[#FDFBF7]">
                  <div className="overline text-[#946f36] mb-3">Category</div>
                  <div className="font-serif-display text-4xl md:text-5xl leading-tight">
                    {c.name}
                  </div>
                  {(c.description || CATEGORY_FALLBACK_DESCRIPTION[c.slug]) && (
                    <p className="mt-3 text-[#D1C3B4] max-w-md text-sm">
                      {c.description || CATEGORY_FALLBACK_DESCRIPTION[c.slug]}
                    </p>
                  )}
                  <span className="mt-6 inline-flex items-center gap-2 overline text-[#946f36] group-hover:text-[#FDFBF7]">
                    Shop now <ArrowUpRight className="w-4 h-4" strokeWidth={1.5} />
                  </span>
                </div>
              </Link>
            ))}
          </div>
        </Container>
      </Section>

      {/* Featured book deep highlight */}
      <Section testId="home-featured-book" className="bg-[#FDFBF7]">
        <Container>
          <div className="grid lg:grid-cols-12 gap-10 items-center">
            <div className="lg:col-span-5 relative">
              <div className="aspect-[3/4] bg-[#F4F1EA] overflow-hidden">
                <img
                  src={featured.image}
                  alt={featured.title}
                  className="w-full h-full object-cover"
                />
              </div>
            </div>
            <div className="lg:col-span-7">
              <Overline>The Featured Volume</Overline>
              <h2 className="mt-4 font-serif-display text-4xl md:text-6xl font-light leading-[1.04]">
                {featured.title}
              </h2>
              <p className="mt-6 text-[#4A3728] leading-relaxed text-lg max-w-2xl">
                {featured.description}
              </p>
              <div className="mt-8 grid sm:grid-cols-3 gap-4 max-w-2xl">
                <Stat label="Pages" value="350" />
                <Stat label="Format" value="Paperback" />
                <Stat label="Launch" value="Sep 2026" />
              </div>
              <div className="mt-10 flex flex-wrap gap-3">
                <Link
                  to="/featured-book"
                  data-testid="home-featured-book-explore"
                  className="bg-[#6e5022] hover:bg-[#946f36] text-[#FDFBF7] px-7 py-3.5 text-xs uppercase tracking-[0.2em] transition-colors"
                >
                  Explore the Book
                </Link>
                <Link
                  to={`/product/${featured.id}`}
                  data-testid="home-featured-book-preorder"
                  className="border border-[#6e5022] text-[#6e5022] hover:bg-[#6e5022] hover:text-[#FDFBF7] px-7 py-3.5 text-xs uppercase tracking-[0.2em] transition-colors"
                >
                  Order Now
                </Link>
              </div>
            </div>
          </div>
        </Container>
      </Section>

      {/* Collectibles preview grid */}
      <Section testId="home-collectibles-preview" className="bg-[#F4F1EA]">
        <Container>
          <div className="flex items-end justify-between flex-wrap gap-6 mb-12">
            <div>
              <Overline>Coming 2027</Overline>
              <h2 className="mt-3 font-serif-display text-4xl md:text-5xl font-light leading-tight">
                Ace Asia Collectibles.
              </h2>
            </div>
            <Link
              to="/shop/collectibles"
              data-testid="home-view-all-collectibles"
              className="overline text-[#6e5022] hover:text-[#946f36]"
            >
              View all →
            </Link>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-8">
            {collectibles.map((p, i) => (
              <ProductCard key={p.id} product={p} index={i} />
            ))}
          </div>
        </Container>
      </Section>

      {/* Multimedia / Podcast feature */}
      <Section testId="home-podcast" className="bg-[#281a12] text-[#FDFBF7] grain relative overflow-hidden">
        <Container className="relative z-10">
          <div className="grid lg:grid-cols-12 gap-12 items-center">
            <div className="lg:col-span-6">
              <div className="overline text-[#946f36] mb-6">
                <span className="gold-rule bg-[#946f36] mr-3" /> Chantal & Co · The Podcast
              </div>
              <h2 className="font-serif-display text-4xl md:text-6xl font-light leading-[1.04]">
                Conversations on heritage, architecture, and the city we are
                losing.
              </h2>
              <p className="mt-6 text-[#D1C3B4] text-lg leading-relaxed max-w-xl">
                Each episode brings together historians, architects and
                conservationists to unpack the balance between rapid
                modernisation and cultural preservation across South-east Asia.
              </p>
              <div className="mt-8 flex flex-wrap gap-3">
                <a
                  href={COMPANY.socials.youtube}
                  target="_blank"
                  rel="noreferrer noopener"
                  data-testid="home-podcast-youtube-link"
                  className="bg-[#946f36] hover:bg-[#FDFBF7] hover:text-[#281a12] text-[#281a12] px-7 py-3.5 text-xs uppercase tracking-[0.2em] transition-colors inline-flex items-center gap-2"
                >
                  <Play className="w-3 h-3" strokeWidth={2} /> Watch on YouTube
                </a>
                <Link
                  to="/multimedia"
                  data-testid="home-podcast-explore-link"
                  className="border border-[#946f36] text-[#946f36] hover:bg-[#946f36] hover:text-[#281a12] px-7 py-3.5 text-xs uppercase tracking-[0.2em] transition-colors"
                >
                  All Episodes
                </Link>
              </div>
            </div>
            <div className="lg:col-span-6">
              <a
                href={COMPANY.socials.youtube}
                target="_blank"
                rel="noreferrer noopener"
                data-testid="home-podcast-thumbnail-link"
                className="group block relative overflow-hidden border border-[#946f36]/30 aspect-square"
              >
                <img
                  src={ASSETS.podcastCover}
                  alt="Chantal & Co podcast"
                  className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-105"
                />
                <div className="absolute inset-0 bg-[#281a12]/40 group-hover:bg-[#281a12]/20 transition-colors flex items-center justify-center">
                  <span className="w-20 h-20 rounded-full border-2 border-[#FDFBF7] flex items-center justify-center bg-[#281a12]/60 backdrop-blur-sm">
                    <Play className="w-8 h-8 text-[#FDFBF7] ml-1" strokeWidth={1.5} />
                  </span>
                </div>
              </a>
            </div>
          </div>
        </Container>
      </Section>

      {/* Newsletter / Contact CTA */}
      <Section testId="home-newsletter" className="bg-[#FDFBF7]">
        <Container>
          <div className="border-t border-b border-[#6e5022]/25 py-16 text-center">
            <Overline className="justify-center inline-block">Stay close</Overline>
            <h2 className="mt-4 font-serif-display text-4xl md:text-5xl font-light leading-tight max-w-2xl mx-auto">
              Heritage stories, podcast drops, and launch invitations.
            </h2>
            <p className="mt-4 text-[#4A3728] max-w-xl mx-auto">
              Join the Ace Asia mailing list — and reach out anytime by email
              or WhatsApp.
            </p>
            <div className="mt-8 flex flex-wrap gap-3 justify-center">
              <Link
                to="/contact"
                data-testid="home-newsletter-contact-link"
                className="bg-[#6e5022] hover:bg-[#946f36] text-[#FDFBF7] px-7 py-3.5 text-xs uppercase tracking-[0.2em] transition-colors"
              >
                Contact Us
              </Link>
              <a
                href={COMPANY.whatsapp}
                target="_blank"
                rel="noreferrer noopener"
                data-testid="home-newsletter-whatsapp-link"
                className="border border-[#6e5022] text-[#6e5022] hover:bg-[#6e5022] hover:text-[#FDFBF7] px-7 py-3.5 text-xs uppercase tracking-[0.2em] transition-colors"
              >
                WhatsApp Us
              </a>
            </div>
          </div>
        </Container>
      </Section>
    </div>
  );
}

function Stat({ label, value }) {
  return (
    <div className="border-l-2 border-[#946f36] pl-4">
      <div className="overline text-[#946f36]">{label}</div>
      <div className="font-serif-display text-2xl text-[#281a12] mt-1">{value}</div>
    </div>
  );
}
